// src/hr_logic/hrUtils.ts
import { toLocalIso } from '../utils/localDate';
/**
 * HR domain calculation engine — pure time/vacation math.
 *
 * All time math uses epoch milliseconds (`number`). Dates are formatted
 * locally (getFullYear/getMonth/getDate) — never via toISOString, which
 * shifts midnight CEST to the previous UTC day.
 */

/** Firestore-style timestamp duck-type (has toDate()/toMillis()). */
export interface HRTimestampLike {
  toDate?: () => Date;
  toMillis?: () => number;
}

/** Union of every timestamp shape an HR log may carry. */
export type HRLogTimestamp = Date | number | string | HRTimestampLike;

/** A single time-tracking log entry (clock-in/out, break, manual correction). */
export interface HRLog {
  id?: string;
  /** 'in' | 'out' | 'break' | 'manual_correction' | 'manual_pause' */
  action?: string;
  timestamp?: HRLogTimestamp | null;
  /** Pre-computed local date (YYYY-MM-DD); falls back to the timestamp. */
  dateString?: string | null;
  /** Pause/correction delta in milliseconds. */
  deltaMs?: number | null;
  comment?: string | null;
  [key: string]: unknown;
}

/** An approved absence (vacation/sick) passed to calculateDailyStats for
 *  per-day credit. Only `status === 'approved'` rows should be passed in —
 *  the function trusts its caller's status filtering. */
export interface PaidAbsence {
  type?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  /** Total day count; 0.5 marks a half-day (credit = half the daily target). */
  days?: number | null;
}

/** Aggregated per-day statistics produced by calculateDailyStats. */
export interface DailyStats {
  date: string;
  logs: HRLog[];
  start: Date | null;
  end: Date | null;
  workMs: number;
  pauseMs: number;
  manualPauseMs: number;
  manualDelta: number;
  corrections: string[];
  /**
   * Every clock-in→clock-out session of the day, in chronological order
   * (epoch ms; `end` is null while the session is still open). Drives the
   * Pres. column display AND the automatic-pause rule: `closedBy` records
   * which action closed the session — gaps after a 'break' count as pause,
   * gaps after an 'out' do NOT (leaving work ≠ taking a break).
   */
  sessions?: { start: number; end: number | null; closedBy?: string }[];
  /** Raw absence type credited for this day (e.g. 'Urlaub'), if any. */
  absenceType?: string;
  /** Credited milliseconds for an approved absence day (target or target/2). */
  absenceCreditMs?: number;
  /** Set after processing. */
  isOngoing?: boolean;
  /** Set after processing: the last action of the day ('in' | 'break' | 'out'). */
  clockStatus?: string;
  /** Running overtime balance in milliseconds (set after processing). */
  zeitkontoMs?: number;
  /** Per-day difference vs. target in milliseconds (set after processing). */
  diffMs?: number;
}

/** An HR absence/vacation request row. */
export interface AbsenceRequest {
  id?: string;
  userId?: string;
  /** e.g. 'urlaub' | 'krankheit' | 'sonderurlaub' */
  type?: string;
  /** Local date strings (YYYY-MM-DD). */
  startDate?: string | null;
  endDate?: string | null;
  halfDay?: boolean;
  status?: string;
  /** Computed vacation-day count (may be fractional for half days). */
  days?: number;
  comment?: string | null;
  [key: string]: unknown;
}

/**
 * App-specific contract details stored inside the `users.data` JSONB column.
 * NOTE: `anspruchNextYear` is stored as a STRING (e.g. "30") — coerce with
 * Number() at the call site; do not change the stored representation.
 */
export interface UserContractDetails {
  weeklyHours?: number;
  targetHoursPerDay?: number;
  /** Mon-first workday array (index 0 = Monday). */
  workdays?: boolean[];
  entryDate?: string | null;
  exitDate?: string | null;
  /** Stored as STRING in users.data JSONB (e.g. "30"). */
  anspruchNextYear?: string;
  /** German Bundesland key (e.g. 'NW') driving public-holiday lookup. */
  holidayState?: string | null;
  [key: string]: unknown;
}

/** A public holiday with its local date string. */
export interface PublicHoliday {
    dateString: string;
    name: string;
}

/**
 * Convert weekly contract hours to the stored per-day value.
 *
 * The working model is edited as "hours per week" in the UI but stored as
 * `target_hours_per_day` (every consumer — daily stats, timesheets, the
 * shift calendar's weeklyHours — reads it per day). Conversion divides by
 * the number of ACTIVE workdays so e.g. 20h/week over 4 workdays = 5h/day.
 * Without active workdays it falls back to 5 (Mon–Fri).
 */
export function weeklyToDailyHours(weeklyHours: number, workdays?: boolean[] | null): number {
    const activeDays = Array.isArray(workdays)
        ? workdays.filter(Boolean).length
        : 5;
    if (activeDays <= 0) return 0;
    return Math.round((weeklyHours / activeDays) * 100) / 100;
}

/** Inverse of weeklyToDailyHours — derives the weekly display value. */
export function dailyToWeeklyHours(hoursPerDay: number | null | undefined, workdays?: boolean[] | null): number {
    const activeDays = Array.isArray(workdays)
        ? workdays.filter(Boolean).length
        : 5;
    if (activeDays <= 0) return 0;
    return Math.round((hoursPerDay ?? 0) * activeDays * 100) / 100;
}

/**
 * Resolve weekly contract hours for a user row.
 *
 * Prefers an explicitly saved `weeklyHours` (top-level or inside `users.data` JSONB,
 * including `0` for Minijobber / on-demand staff) and falls back to deriving it
 * from `target_hours_per_day * activeWorkdays` for legacy rows.
 */
export function resolveWeeklyHours(
    user?: {
        weeklyHours?: number | null;
        target_hours_per_day?: number | null;
        targetHoursPerDay?: number | null;
        workdays?: boolean[] | null;
        data?: {
            weeklyHours?: number | null;
            targetHoursPerDay?: number | null;
            workdays?: boolean[] | null;
        } | null;
    } | null,
): number {
    const explicit = user?.weeklyHours ?? user?.data?.weeklyHours;
    if (typeof explicit === 'number' && Number.isFinite(explicit) && explicit >= 0) {
        return Math.round(explicit * 100) / 100;
    }
    const workdays = Array.isArray(user?.workdays)
        ? user?.workdays
        : Array.isArray(user?.data?.workdays)
            ? user?.data?.workdays
            : null;
    const rawDaily = user?.target_hours_per_day ?? user?.targetHoursPerDay ?? user?.data?.targetHoursPerDay;
    const daily = typeof rawDaily === 'number' && Number.isFinite(rawDaily) && rawDaily >= 0 ? rawDaily : 8;
    return dailyToWeeklyHours(daily, workdays);
}

export function getTimestampMillis(log: HRLog | null | undefined): number {
    if (!log) return 0;
    const ts = log.timestamp;
    if (!ts) {
        return Date.now();
    }
    if (typeof ts === 'object' && 'toMillis' in ts && typeof ts.toMillis === 'function') {
        return ts.toMillis();
    }
    if (ts instanceof Date) {
        return ts.getTime();
    }
    if (typeof ts === 'number') {
        return ts;
    }
    if (typeof ts === 'string') {
        return new Date(ts).getTime();
    }
    if (typeof ts === 'object' && 'toDate' in ts && typeof ts.toDate === 'function') {
        return ts.toDate().getTime();
    }
    return Date.now();
}

export function getTimestampDate(log: HRLog | null | undefined): Date {
    if (!log) return new Date();
    const ts = log.timestamp;
    if (!ts) {
        return new Date();
    }
    let d: Date;
    if (typeof ts === 'object' && 'toDate' in ts && typeof ts.toDate === 'function') {
        d = ts.toDate();
    } else if (ts instanceof Date) {
        d = ts;
    } else if (typeof ts === 'number') {
        d = new Date(ts);
    } else if (typeof ts === 'string') {
        d = new Date(ts);
    } else {
        d = new Date();
    }
    if (isNaN(d.getTime())) {
        return new Date();
    }
    return d;
}

export function formatTimeShort(ms: number | null | undefined): string {
    if (!ms) return '-';
    const sign = ms < 0 ? '-' : '+';
    const totalMinutes = Math.floor(Math.abs(ms) / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${sign}${hours.toString().padStart(2, '0')}h ${minutes.toString().padStart(2, '0')}m`;
}

export function calculateDailyStats(
    logsArray: HRLog[] | null | undefined,
    targetHoursPerDay: number = 8,
    workdays: boolean[] = [true, true, true, true, true, false, false],
    absences?: PaidAbsence[],
): DailyStats[] {
    const TARGET_WORK_TIME_MS = (targetHoursPerDay || 8) * 60 * 60 * 1000;
    const days: Record<string, DailyStats> = {};

    // Guard against undefined/null while useQuery is loading
    if (!logsArray || !Array.isArray(logsArray)) return [];

    // Helper to check if a date is a workday (parses YYYY-MM-DD as local date
    // to avoid UTC timezone drift in getDay()).
    const isWorkday = (dateStr: string): boolean => {
        const [y, m, d] = String(dateStr).split('-').map(Number);
        const date = new Date(y, (m || 1) - 1, d || 1);
        const dow = date.getDay(); // 0=Sun..6=Sat
        const idx = (dow + 6) % 7; // 0=Mon..6=Sun
        return workdays[idx] === true;
    };
    const sortedLogs = [...logsArray].sort((a, b) => getTimestampMillis(a) - getTimestampMillis(b));

    // Approved paid-absence days (Urlaub / Krankheit) credit the day's target
    // hours so a pure absence day nets 0 and hotfix work becomes overtime.
    // Weekends are skipped (no target → no credit). Only PAID absence types
    // credit — Freizeitausgleich spends accumulated overtime instead.
    const PAID_ABSENCE_TYPES = new Set(['Urlaub', 'Krankheit']);
    // FUTURE-dated absence days are deliberately NOT synthesized/credited: a
    // 3-month-out vacation would otherwise fill the top of the newest-first
    // history table. Each day gets its row on the day it actually happens.
    const todayStr = formatLocalDateString(new Date());
    const absenceByDate: Record<string, { type: string; days: number }> = {};
    (absences || []).forEach(a => {
        const type = a.type || '';
        if (!PAID_ABSENCE_TYPES.has(type)) return;
        if (!a.startDate || !a.endDate) return;
        const [sy, sm, sd] = String(a.startDate).split('-').map(Number);
        const [ey, em, ed] = String(a.endDate).split('-').map(Number);
        if (!sy || !sm || !sd || !ey || !em || !ed) return;
        const cursor = new Date(sy, sm - 1, sd);
        const endDate = new Date(ey, em - 1, ed);
        let guard = 0;
        while (cursor <= endDate && guard < 366) {
            const dateStr = formatLocalDateString(cursor);
            if (dateStr <= todayStr && isWorkday(dateStr) && !absenceByDate[dateStr]) {
                absenceByDate[dateStr] = { type, days: (a.days ?? 1) === 0.5 ? 0.5 : 1 };
            }
            cursor.setDate(cursor.getDate() + 1);
            guard++;
        }
    });
    // Synthesize a day row for absence workdays that have NO time logs at all
    // (a full vacation day produces no logs — without this the day would be
    // invisible in the history table and its credit would never apply).
    Object.keys(absenceByDate).forEach(dateStr => {
        if (!days[dateStr]) {
            days[dateStr] = {
                date: dateStr,
                logs: [],
                start: null,
                end: null,
                workMs: 0,
                pauseMs: 0,
                manualPauseMs: 0,
                manualDelta: 0,
                corrections: []
            };
        }
    });

    sortedLogs.forEach(log => {
        const dateStr = log.dateString || (log.timestamp ? formatLocalDateString(getTimestampDate(log)) : null);
        if (!dateStr) return;
        if (!days[dateStr]) {
            days[dateStr] = {
                date: dateStr,
                logs: [],
                start: null,
                end: null,
                workMs: 0,
                pauseMs: 0,
                manualPauseMs: 0,
                manualDelta: 0,
                corrections: []
            };
        }
        days[dateStr].logs.push(log);

        if (log.action === 'in' && !days[dateStr].start) {
            days[dateStr].start = getTimestampDate(log);
        }
        if (log.action === 'manual_correction') {
            days[dateStr].manualDelta += (log.deltaMs || 0);
            if (log.comment) days[dateStr].corrections.push(log.comment);
        }
        if (log.action === 'manual_pause') {
            days[dateStr].pauseMs += (log.deltaMs || 0);
            days[dateStr].manualPauseMs += (log.deltaMs || 0);
            days[dateStr].workMs -= (log.deltaMs || 0);
            if (log.comment) days[dateStr].corrections.push(`Pause: ${log.comment}`);
        }
        if (log.action === 'payout') {
            const delta = log.deltaMs != null ? (log.deltaMs > 0 ? -log.deltaMs : log.deltaMs) : 0;
            days[dateStr].manualDelta += delta;
            days[dateStr].corrections.push(log.comment ? `Auszahlung: ${log.comment}` : 'Auszahlung');
        }
        if (log.action === 'on_call_credit') {
            const delta = log.deltaMs || 0;
            days[dateStr].manualDelta += delta;
            days[dateStr].corrections.push(log.comment ? `Rufbereitschaft: ${log.comment}` : `Rufbereitschaft: +${formatTimeShort(delta)}`);
        }
        if (log.action === 'shift_bonus') {
            const delta = log.deltaMs || 0;
            days[dateStr].manualDelta += delta;
            days[dateStr].corrections.push(log.comment ? `Zuschlag: ${log.comment}` : `Sonntags-/Feiertagszuschlag: +${formatTimeShort(delta)}`);
        }
    });

    let runningZeitkonto = 0;
    const processedDays = Object.values(days).map(day => {
        let currentIn: number | null = null;
        // Sessions mirror the workMs accumulation below so the Pres. column can
        // show every in→out pair of the day ("10:10 — 13:52 / 15:10 — 17:15").
        // `closedBy` records WHICH action closed the session — it drives the
        // automatic-pause rule further below. An open session (no closing
        // 'out'/'break' yet) keeps end = null.
        const sessions: { start: number; end: number | null; closedBy?: string }[] = [];
        day.logs.forEach(log => {
            const t = getTimestampMillis(log);
            if (log.action === 'in') {
                // Guard against double clock-in: if a previous session was
                // never closed (no 'out'/'break' before the next 'in'),
                // count the orphaned session as worked time rather than
                // silently discarding it (which inflates automaticPause).
                if (currentIn) {
                    day.workMs += (t - currentIn);
                    const open = sessions[sessions.length - 1];
                    if (open && open.end === null) { open.end = t; open.closedBy = 'in'; }
                }
                currentIn = t;
                sessions.push({ start: t, end: null });
                day.end = null;
            }
            if ((log.action === 'out' || log.action === 'break') && currentIn) {
                day.workMs += (t - currentIn);
                const open = sessions[sessions.length - 1];
                if (open && open.end === null) { open.end = t; open.closedBy = log.action; }
                currentIn = null;
            }
            if (log.action === 'out') {
                day.end = getTimestampDate(log);
            }
        });

        const todayIso = toLocalIso(new Date());
        const lastLog = day.logs.length > 0 ? day.logs[day.logs.length - 1] : null;
        const isOngoing = day.date === todayIso && lastLog && (lastLog.action === 'in' || lastLog.action === 'break');

        if (isOngoing) {
            if (currentIn) {
                day.workMs += (Date.now() - currentIn);
            }
        }
        day.sessions = sessions;

        day.workMs += day.manualDelta;

        // Automatic pause counts ONLY gaps that follow an explicit 'break'
        // action. Gaps after an 'out' are the user's own free time (e.g. left
        // at 16:00 and returned at 19:00 for a hotfix) and are deliberately
        // NOT booked as break — clock-out means work ended, not a pause.
        // Explicit pauses still come from 'break' logs and manual_pause
        // entries (manualPauseMs is added in the log pass above).
        let automaticPause = 0;
        for (let i = 0; i < sessions.length; i++) {
            const s = sessions[i];
            if (s.closedBy !== 'break' || s.end === null) continue;
            const isLast = i === sessions.length - 1;
            const gapEnd = isLast
                ? (isOngoing ? Date.now() : (day.end ? day.end.getTime() : null))
                : sessions[i + 1].start;
            if (gapEnd === null || gapEnd <= s.end) continue;
            automaticPause += (gapEnd - s.end);
        }
        day.pauseMs += automaticPause;

        day.isOngoing = !!isOngoing;
        day.clockStatus = isOngoing && lastLog ? lastLog.action : 'out';

        return day;
    });

    processedDays.sort((a, b) => a.date.localeCompare(b.date)).forEach(day => {
        // For non-workdays, target is 0 (any work counts as overtime)
        const dayTargetMs = isWorkday(day.date) ? TARGET_WORK_TIME_MS : 0;
        // Approved paid-absence day: credit the (full or half) daily target on
        // top of any actual clocked work, so diff = work + credit − target.
        // A pure absence day (work 0) nets 0; a hotfix on vacation nets overtime.
        const absence = absenceByDate[day.date];
        const absenceCreditMs = absence
            ? (absence.days === 0.5 ? Math.round(TARGET_WORK_TIME_MS / 2) : TARGET_WORK_TIME_MS)
            : 0;
        if (absence) {
            day.absenceType = absence.type;
            day.absenceCreditMs = absenceCreditMs;
            day.corrections.push(`${absence.type}: ${formatTimeShort(absenceCreditMs)}`);
        }
        const diffMs = day.workMs + absenceCreditMs - dayTargetMs;
        runningZeitkonto += diffMs;
        day.zeitkontoMs = runningZeitkonto;
        day.diffMs = diffMs;
    });

    return processedDays.sort((a, b) => b.date.localeCompare(a.date));
}

export const DEFAULT_HOLIDAY_WEIGHTS: Record<string, number> = {
    "Neujahr": 0,
    "Heilige Drei Könige": 0,
    "Internationaler Frauentag": 0,
    "Karfreitag": 0,
    "Ostermontag": 0,
    "Tag der Arbeit": 0,
    "Christi Himmelfahrt": 0,
    "Pfingstmontag": 0,
    "Fronleichnam": 0,
    "Mariä Himmelfahrt": 0,
    "Weltkindertag": 0,
    "Tag der Deutschen Einheit": 0,
    "Reformationstag": 0,
    "Allerheiligen": 0,
    "Buß- und Bettag": 0,
    "Heiligabend": 0.5,
    "Silvester": 0.5,
    "1. Weihnachtstag": 0,
    "2. Weihnachtstag": 0
};

export function formatLocalDateString(date: Date): string {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}

export function getEasterSunday(year: number): Date {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const L = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * L) / 451);
    const month = Math.floor((h + L - 7 * m + 114) / 31);
    const day = ((h + L - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
}

export function getPublicHolidays(year: number, state: string | null | undefined): PublicHoliday[] {
    const holidays: PublicHoliday[] = [];
    const addHoliday = (date: Date, name: string): void => {
        holidays.push({ dateString: formatLocalDateString(date), name });
    };

    addHoliday(new Date(year, 0, 1), "Neujahr");

    if (['BW', 'BY', 'ST'].includes(state || '')) {
        addHoliday(new Date(year, 0, 6), "Heilige Drei Könige");
    }

    if (['BE', 'MV'].includes(state || '')) {
        addHoliday(new Date(year, 2, 8), "Internationaler Frauentag");
    }

    const easter = getEasterSunday(year);

    const karfreitag = new Date(easter);
    karfreitag.setDate(easter.getDate() - 2);
    addHoliday(karfreitag, "Karfreitag");

    const ostermontag = new Date(easter);
    ostermontag.setDate(easter.getDate() + 1);
    addHoliday(ostermontag, "Ostermontag");

    addHoliday(new Date(year, 4, 1), "Tag der Arbeit");

    const christiHimmelfahrt = new Date(easter);
    christiHimmelfahrt.setDate(easter.getDate() + 39);
    addHoliday(christiHimmelfahrt, "Christi Himmelfahrt");

    const pfingstmontag = new Date(easter);
    pfingstmontag.setDate(easter.getDate() + 50);
    addHoliday(pfingstmontag, "Pfingstmontag");

    if (['BW', 'BY', 'HE', 'NW', 'RP', 'SL'].includes(state || '')) {
        const fronleichnam = new Date(easter);
        fronleichnam.setDate(easter.getDate() + 60);
        addHoliday(fronleichnam, "Fronleichnam");
    }

    if (['SL', 'BY'].includes(state || '')) {
        addHoliday(new Date(year, 7, 15), "Mariä Himmelfahrt");
    }

    if (state === 'TH') {
        addHoliday(new Date(year, 8, 20), "Weltkindertag");
    }

    addHoliday(new Date(year, 9, 3), "Tag der Deutschen Einheit");

    if (['BB', 'HB', 'HH', 'MV', 'NI', 'SN', 'ST', 'SH', 'TH'].includes(state || '')) {
        addHoliday(new Date(year, 9, 31), "Reformationstag");
    }

    if (['BW', 'BY', 'NW', 'RP', 'SL'].includes(state || '')) {
        addHoliday(new Date(year, 10, 1), "Allerheiligen");
    }

    if (state === 'SN') {
        const nov23 = new Date(year, 10, 23);
        const dayOfWeek = nov23.getDay();
        const diff = dayOfWeek >= 3 ? dayOfWeek - 3 : dayOfWeek + 4;
        const bussUndBettag = new Date(nov23);
        bussUndBettag.setDate(nov23.getDate() - diff);
        addHoliday(bussUndBettag, "Buß- und Bettag");
    }

    addHoliday(new Date(year, 11, 24), "Heiligabend");
    addHoliday(new Date(year, 11, 31), "Silvester");

    addHoliday(new Date(year, 11, 25), "1. Weihnachtstag");
    addHoliday(new Date(year, 11, 26), "2. Weihnachtstag");

    return holidays;
}

export function isWeekend(date: Date): boolean {
    const day = date.getDay();
    return day === 0 || day === 6;
}

export function isHoliday(date: Date, state: string | null | undefined): boolean {
    if (!state) return false;
    const year = date.getFullYear();
    const holidays = getPublicHolidays(year, state);
    const dateStr = formatLocalDateString(date);
    return holidays.some(h => h.dateString === dateStr);
}

export function getHolidayName(date: Date, state: string | null | undefined): string | null {
    if (!state) return null;
    const year = date.getFullYear();
    const holidays = getPublicHolidays(year, state);
    const dateStr = formatLocalDateString(date);
    const found = holidays.find(h => h.dateString === dateStr);
    return found ? found.name : null;
}

/** All 16 German Bundesländer (ISO 3166-2 codes). */
export const GERMAN_STATES = ['BW', 'BY', 'BE', 'BB', 'HB', 'HH', 'HE', 'MV', 'NI', 'NW', 'RP', 'SL', 'SN', 'ST', 'SH', 'TH'] as const;

export interface HolidayAcrossStates {
    name: string;
    states: string[];
    /** True when every Bundesland observes the holiday. */
    nationwide: boolean;
}

/**
 * Holidays on one date across ALL Bundesländer, grouped by name with the set
 * of states observing them — so a calendar can attribute a holiday to its
 * regions or mark it nationwide.
 */
export function getHolidaysAcrossStates(date: Date): HolidayAcrossStates[] {
    const byName = new Map<string, string[]>();
    for (const state of GERMAN_STATES) {
        const name = getHolidayName(date, state);
        if (!name) continue;
        const list = byName.get(name);
        if (list) list.push(state);
        else byName.set(name, [state]);
    }
    return [...byName.entries()].map(([name, states]) => ({
        name,
        states,
        nationwide: states.length === GERMAN_STATES.length,
    }));
}

export function calculateVacationDays(
    startDateStr: string | null | undefined,
    endDateStr: string | null | undefined,
    state: string | null | undefined,
    isHalfDay: boolean = false,
    hrSettings: { holidayWeights?: Record<string, number> } | null | undefined = {},
    workdays: boolean[] = [true, true, true, true, true, false, false],
): number {
    if (!startDateStr || !endDateStr) return 0;

    const weights = hrSettings?.holidayWeights || DEFAULT_HOLIDAY_WEIGHTS;

    /**
     * Checks if a date is a primary workday for this person.
     * Maps JS getDay() (0=Sun..6=Sat) to our Mon-first workdays array.
     */
    const isWorkday = (date: Date): boolean => {
        const dow = date.getDay();
        const idx = (dow + 6) % 7;  // 0=Mon..6=Sun
        return workdays[idx] === true;
    };

    if (isHalfDay) {
        const date = new Date(startDateStr);
        if (!isWorkday(date)) return 0;

        const holName = getHolidayName(date, state);
        if (holName) {
            const weight = weights[holName] !== undefined ? weights[holName] : 0;
            return Math.min(0.5, weight);
        }
        return 0.5;
    }

    const current = new Date(startDateStr);
    const end = new Date(endDateStr);
    let count = 0;

    while (current <= end) {
        if (isWorkday(current)) {
            const holName = getHolidayName(current, state);
            if (holName) {
                const weight = weights[holName] !== undefined ? weights[holName] : 0;
                count += weight;
            } else {
                count += 1.0;
            }
        }
        current.setDate(current.getDate() + 1);
    }
    return count;
}

export function calculateProRatedEntitlement(
    annualEntitlement: number,
    entryDateStr: string | null | undefined,
    exitDateStr: string | null | undefined,
    year: number,
    workdays: boolean[] = [true, true, true, true, true, false, false],
): number {
    if (!entryDateStr && !exitDateStr) {
        // No employment-period proration; apply part-time workday ratio only.
        return applyPartTimeRatio(annualEntitlement, workdays);
    }

    const entryDate = entryDateStr ? new Date(entryDateStr) : new Date(year, 0, 1);
    const exitDate = exitDateStr ? new Date(exitDateStr) : new Date(year, 11, 31);

    const yearStart = new Date(year, 0, 1);
    const yearEnd = new Date(year, 11, 31);

    const activeStart = entryDate > yearStart ? entryDate : yearStart;
    const activeEnd = exitDate < yearEnd ? exitDate : yearEnd;

    if (activeStart > activeEnd) return 0;

    let fullMonths = 0;
    for (let m = 0; m < 12; m++) {
        const firstDayOfMonth = new Date(year, m, 1);
        const lastDayOfMonth = new Date(year, m + 1, 0);

        const isEmployedAtStart = entryDate <= firstDayOfMonth;
        const isEmployedAtEnd = exitDate >= lastDayOfMonth;

        if (isEmployedAtStart && isEmployedAtEnd) {
            fullMonths++;
        }
    }

    const proRated = (annualEntitlement / 12) * fullMonths;
    return applyPartTimeRatio(proRated, workdays);
}

/**
 * Applies the German part-time proration ratio (Bundesurlaubsgesetz § 5).
 * A worker on N workdays/week receives N/5 of a full-time entitlement.
 * The statutory reference week is 5 days, so a 4-day worker gets 4/5 = 0.8×.
 *
 * @param entitlement - Full-time-equivalent entitlement.
 * @param workdays - Mon-first workday array.
 * @returns Part-time-adjusted entitlement, rounded to 0.5 days.
 */
function applyPartTimeRatio(entitlement: number, workdays: boolean[] | null | undefined): number {
    const workdayCount = (workdays || []).filter(Boolean).length;
    if (workdayCount <= 0 || workdayCount >= 5) {
        return Math.round(entitlement * 2) / 2;
    }
    const ratio = workdayCount / 5;
    const adjusted = entitlement * ratio;
    return Math.round(adjusted * 2) / 2;
}
